import React, { ReactElement, useEffect, useRef, useState } from "react";
import { useTranslate } from "@obsbot/remo-i18n";
import { eventBus, filterInputValue, limitInputLength } from "@obsbot/remo-utils";
import classNames from "classnames";
import { InputWithError } from "../InputWithErrorMsg";
import { CustomModal } from "../CustomModal";
import { withMessage } from "../../HOC/withMessage";
import "./index.less";

export enum ActionType {
    Edit = "edit",
    Create = "create",
    Delete = "delete",
    DeleteHasMember = "deleteHasMember",
    DeleteJoin = "deleteJoin",
    Quit = "quit",
    Switch = "switch",
}
export interface ChannelActionProps {
    actionType: ActionType;
    visible: boolean;
    channelId: string;
    channelName: string;
    isCreateFailed?: boolean;
    hasMember?: boolean;
    onDone: (channelId: string, type: ActionType, channelName: string) => void;
    setIsCreateFailed?: (flag: boolean) => void;
    setVisible: (flag: boolean) => void;
}

export const ChannelActionModal: React.FC<ChannelActionProps> = withMessage(
    ({
        visible,
        actionType,
        channelName,
        channelId,
        isCreateFailed,
        hasMember,
        onDone,
        setIsCreateFailed,
        setVisible,
        message,
    }) => {
        const t = useTranslate();
        // 记录当前操作的频道名称
        const [currentChannelName, setCurrentChannelName] = useState<string>(channelName);
        // 是否正在输入拼音
        const isComposing = useRef(false);
        useEffect(() => {
            setCurrentChannelName(channelName);
        }, [channelName, visible]);
        // 处理onDone事件
        const handleOnDone = (): void => {
            if (!currentChannelName) {
                message.warning(t("noInputSingle", { text1: t("channelName") }));
                return;
            }
            onDone && onDone(channelId, actionType, currentChannelName);
        };
        // 渲染底部按钮
        const renderFooter = (actionType: ActionType): ReactElement => {
            switch (actionType) {
                case ActionType.Edit: {
                    return (
                        <div className="modal-button-item" onClick={handleOnDone}>
                            {t("confirm")}
                        </div>
                    );
                }
                case ActionType.Create: {
                    return (
                        <div
                            className="modal-button-item modal-button-item-full"
                            onClick={handleOnDone}
                        >
                            {t("confirm")}
                        </div>
                    );
                }
                case ActionType.Delete:
                case ActionType.DeleteHasMember:
                case ActionType.DeleteJoin: {
                    return (
                        <div className="modal-button-group">
                            <div
                                className="modal-button-item button-normal"
                                onClick={() => {
                                    setVisible && setVisible(false);
                                }}
                            >
                                {t("cancel")}
                            </div>
                            <div
                                className="modal-button-item button-danger"
                                onClick={() => onDone && onDone(channelId, actionType, "")}
                            >
                                {t("delete")}
                            </div>
                        </div>
                    );
                }
                case ActionType.Quit: {
                    return (
                        <div className="modal-button-group">
                            <div
                                className="modal-button-item button-normal"
                                onClick={() => {
                                    setCurrentChannelName(channelName);
                                    setVisible && setVisible(false);
                                }}
                            >
                                {t("cancel")}
                            </div>
                            <div
                                className="modal-button-item button-danger"
                                onClick={() => onDone && onDone(channelId, actionType, "")}
                            >
                                {t("confirm")}
                            </div>
                        </div>
                    );
                }
                case ActionType.Switch: {
                    return (
                        <div className="modal-button-group">
                            <div
                                className="modal-button-item button-normal"
                                onClick={() => {
                                    setCurrentChannelName(channelName);
                                    setVisible && setVisible(false);
                                }}
                            >
                                {t("cancel")}
                            </div>
                            <div
                                className="modal-button-item"
                                onClick={() => onDone && onDone(channelId, actionType, "")}
                            >
                                {t("confirm")}
                            </div>
                        </div>
                    );
                }
            }
        };
        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
            if (e.key === "Enter") {
                if (ActionType.Create === actionType || ActionType.Edit === actionType) {
                    handleOnDone();
                    return;
                }
                onDone && onDone(channelId, actionType, currentChannelName);
            }
        };
        // 渲染主体内容部分
        const renderContent = (actionType: ActionType): ReactElement => {
            switch (actionType) {
                case ActionType.Quit:
                case ActionType.Delete:
                case ActionType.DeleteHasMember:
                case ActionType.DeleteJoin:
                case ActionType.Switch: {
                    if (actionType === ActionType.Delete && hasMember) {
                        actionType = ActionType.DeleteHasMember;
                    }
                    return (
                        <div className="action-modal-content action-modal-warn-text text-shadow">
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: t(actionType + "ChannelContent", {
                                        channelName: `<strong>${channelName}</strong>`,
                                    }),
                                }}
                            />
                        </div>
                    );
                }
                case ActionType.Create:
                case ActionType.Edit: {
                    return (
                        <div className="action-modal-content">
                            <div className="action-modal-form-title">{t("channelName")}</div>
                            <InputWithError
                                // eslint-disable-next-line jsx-a11y/no-autofocus
                                autoFocus={true}
                                className="action-modal-form-input"
                                errorMsg={t("createChannelFail")}
                                isError={isCreateFailed || false}
                                maxLength={16}
                                placeholder={t("enterChannelName")}
                                status={isCreateFailed ? "error" : ""}
                                value={currentChannelName}
                                onChange={e => {
                                    let filterVal = filterInputValue(e.target.value);
                                    if (!isComposing.current) {
                                        const { val, overLimit } = limitInputLength(filterVal);
                                        if (overLimit) {
                                            eventBus.emit("show-message", {
                                                message: t("nameLengthOverLimit"),
                                                type: "warning",
                                            });
                                        }
                                        filterVal = val;
                                    }
                                    setCurrentChannelName(filterVal);
                                    setIsCreateFailed && setIsCreateFailed(false);
                                }}
                                onCompositionEnd={e => {
                                    isComposing.current = false;
                                    const finalValue = (e.target as any).value;
                                    const { val, overLimit } = limitInputLength(finalValue);
                                    if (overLimit) {
                                        eventBus.emit("show-message", {
                                            message: t("nameLengthOverLimit"),
                                            type: "warning",
                                        });
                                    }
                                    setCurrentChannelName(val);
                                }}
                                onCompositionStart={() => (isComposing.current = true)}
                                onFocus={() => setIsCreateFailed && setIsCreateFailed(false)}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                    );
                }
            }
        };
        const isEditOrCreate = actionType === ActionType.Create || actionType === ActionType.Edit;
        return (
            <CustomModal
                destroyOnHidden={true}
                open={visible}
                renderHeader={
                    <div
                        className={classNames("modal-header action-modal-header", {
                            "text-shadow": !isEditOrCreate,
                        })}
                    >
                        <span>{t(actionType + "Channel")}</span>
                    </div>
                }
                setVisible={setVisible}
                // eslint-disable-next-line react/jsx-sort-props
                onClickClose={() => setCurrentChannelName(channelName)}
                className="ChannelActionModal"
                // closable={isEditOrCreate}
                footer={renderFooter(actionType)}
            >
                {renderContent(actionType)}
            </CustomModal>
        );
    },
);
